import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { ZodError } from 'zod'
import { type RawStoreData } from './stores/store-types'
// For server-side HTML parsing
import * as cheerio from 'cheerio'

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export function toTitleCase(str: string) {
	return str.replace(/\b\w/g, (char) => char.toUpperCase())
}

export function isDate(value: unknown): value is Date {
	return value instanceof Date
}

export function buildZodParsingErrorString(error: ZodError) {
	return error.issues
		.map((issue) => {
			const path = issue.path.join('.')
			return `${path}: ${issue.message}`
		})
		.join('\n')
}

export function omit<T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
	const result = { ...obj }
	keys.forEach((key) => delete result[key])
	return result
}

export function cleanStoreNumber(storeNumber: string) {
	return storeNumber.replace(/[^a-zA-Z0-9]/g, '')
}

export function cleanStringForCsv(str: string) {
	return str.replace(/"/g, '""')
}

export function isStoreVeryClosed(store: RawStoreData): boolean {
	return store.openInfo === 'CLOSED_PERMANENTLY' || store.openInfo === 'CLOSED_TEMPORARILY'
}

export function prepareHtmlForAboutSection(html: string): string {
	// Check if we're in a browser environment with DOM support
	if (typeof window !== 'undefined' && window && typeof DOMParser !== 'undefined') {
		// Use browser's DOMParser for HTML parsing
		const parser = new DOMParser()
		const doc = parser.parseFromString(html, 'text/html')

		// Find all anchor elements and add the appropriate attributes
		doc.querySelectorAll('a').forEach((anchor) => {
			// Add target="_blank" if it doesn't exist
			if (!anchor.hasAttribute('target')) {
				anchor.setAttribute('target', '_blank')
			}

			// Handle the rel attribute
			if (!anchor.hasAttribute('rel')) {
				// If no rel attribute exists, add it
				anchor.setAttribute('rel', 'noopener')
			} else {
				// If rel exists, make sure it contains both noopener and noreferrer
				const relValues = anchor.getAttribute('rel')!.split(' ')
				let modified = false

				if (!relValues.includes('noopener')) {
					relValues.push('noopener')
					modified = true
				}

				if (modified) {
					anchor.setAttribute('rel', relValues.join(' '))
				}
			}
		})

		// Return the updated HTML
		return doc.body.innerHTML
	} else {
		// Use cheerio for server-side HTML parsing
		const $ = cheerio.load(html)

		// Find all anchor elements and add the appropriate attributes
		$('a').each((_, element) => {
			const anchor = $(element)

			// Add target="_blank" if it doesn't exist
			if (!anchor.attr('target')) {
				anchor.attr('target', '_blank')
			}

			// Handle the rel attribute
			if (!anchor.attr('rel')) {
				// If no rel attribute exists, add it
				anchor.attr('rel', 'noopener')
			} else {
				// If rel exists, make sure it contains both noopener and noreferrer
				const relValues = (anchor.attr('rel') || '').split(' ')
				let modified = false

				if (!relValues.includes('noopener')) {
					relValues.push('noopener')
					modified = true
				}

				if (modified) {
					anchor.attr('rel', relValues.join(' '))
				}
			}
		})

		// Return the updated HTML
		return $.html()
	}
}

/**
 * Combines street and street2 fields to create a full street address
 *
 * @param address - Object containing street and optional street2 properties
 * @returns Full street address as a string
 */
export function getFullStreetAddress(address: { street: string; street2?: string }): string {
	const trimmedStreet = address.street.trim()

	if (!address.street2 || address.street2.trim() === '') {
		return trimmedStreet
	}

	const trimmedStreet2 = address.street2.trim()
	return `${trimmedStreet} ${trimmedStreet2}`
}
